import { useQuery } from "@tanstack/react-query";
import type { StudentListFilters } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { studentKeys, buildStudentsQuery } from "@/hooks/useStudents";

export interface StudentLearningSummaryItem {
  student_id: string;
  student_name: string;
  user_id: string | null;
  has_portal_login: boolean;
  active_plans: number;
  due_cards: number;
  last_active_at: string | null;
  streak_days: number;
}

export interface StudentLearningSummaryListResponse {
  items: StudentLearningSummaryItem[];
  total: number;
}

export interface CoachSummarySnapshot {
  due_now: number;
  reviewed_today: number;
  streak_days: number;
  active_plans: number;
  cards_total: number;
}

export interface StudentLearningDetailResponse {
  student_id: string;
  user_id: string | null;
  has_portal_login: boolean;
  coach_summary: CoachSummarySnapshot | null;
  pathways: Array<{
    id: string;
    title: string;
    purpose: string;
    status: string;
    onboarding_complete: boolean;
    completion_percent: number | null;
  }>;
  recent_activity: Array<{ id: string; title: string; activity_type: string; occurred_at: string }>;
  certificates: Array<{
    id: string;
    subject: string;
    topic: string;
    score: number;
    issued_at: string;
  }>;
  reading_logs: Array<{
    id: string;
    subject: string;
    topic: string;
    minutes_spent: number | null;
    logged_at: string;
  }>;
}

export const studentLearningKeys = {
  summary: (filters?: StudentListFilters) =>
    [...studentKeys.all, "learning-summary", filters ?? {}] as const,
  detail: (studentId: string | null) => [...studentKeys.all, "learning-detail", studentId] as const,
};

export function useStudentsLearningSummary(
  filters: StudentListFilters | undefined,
  enabled: boolean,
) {
  const qs = buildStudentsQuery(filters);
  return useQuery<StudentLearningSummaryListResponse>({
    queryKey: studentLearningKeys.summary(filters),
    queryFn: () =>
      api.get<StudentLearningSummaryListResponse>(
        `/api/students/learning-summary${qs ? `?${qs}` : ""}`,
      ),
    enabled,
    staleTime: 15_000,
  });
}

export function useStudentLearningDetail(studentId: string | null, enabled: boolean) {
  return useQuery<StudentLearningDetailResponse>({
    queryKey: studentLearningKeys.detail(studentId),
    queryFn: () =>
      api.get<StudentLearningDetailResponse>(`/api/students/${studentId}/learning-detail`),
    enabled: enabled && Boolean(studentId),
    staleTime: 30_000,
  });
}
